import * as i0 from '@angular/core';
import { Injectable, Directive, Input, Pipe, NgModule } from '@angular/core';
import { NG_VALIDATORS } from '@angular/forms';

/**
 * This error will throw when input value of the service method is not a valid value.
 */
class InvalidServiceInputError extends Error {
    constructor() {
        super('Invalid value has been passed to the service.');
    }
}

class PersianNumberService {
    /**
     * Splits input value by persian letters and then returns length of matched array.
     *
     * @param value
     * @return 0 if input value has no persian letter, numbers of persian letters in the input value otherwise.
     */
    static _getMatchedPatternLength(value) {
        // Language=JSRegexp
        const matchResult = value.match(new RegExp(`[${PersianNumberService.persianNumberPattern}]`, 'g'));
        return matchResult ? matchResult.length : 0;
    }
    /**
     * Checks if input value contains any persian number or not.
     *
     * @param value
     * @return true if input value contains any persian number. false otherwise.
     */
    containsPersian(value) {
        if (!value) {
            return false;
        }
        // Language=JSRegexp
        const persianRegex = new RegExp(`[${PersianNumberService.persianNumberPattern}]`);
        return persianRegex.test(value);
    }
    /**
     * Checks if input value contains only persian letters.
     *
     * @param value
     */
    isPersian(value) {
        if (!value) {
            return false;
        }
        return value.length === PersianNumberService._getMatchedPatternLength(value);
    }
    /**
     * Converts arabic numbers to the persian ones.
     *
     * @param value
     */
    arabicToPersian(value) {
        return value.replace(/٤/g, PersianNumberService.persianNumbersTable[4]).replace(/٥/g, PersianNumberService.persianNumbersTable[5]).replace(/٦/g, PersianNumberService.persianNumbersTable[6]);
    }
    /**
     * Converts all arabic and english numbers to the persian numbers.
     *
     * @param value
     */
    toPersian(value) {
        if (value === undefined || value === null) {
            throw new InvalidServiceInputError();
        }
        let persianValue = value.toString();
        persianValue = this.arabicToPersian(persianValue);
        let regex;
        for (let i = 0; i < PersianNumberService.persianNumbersTable.length; i++) {
            // Language=JSRegexp
            regex = new RegExp(`[${PersianNumberService.englishNumbersTable[i]}]`, 'g');
            persianValue = persianValue.replace(regex, PersianNumberService.persianNumbersTable[i]);
        }
        return persianValue;
    }
    /**
     * Convert persian numbers in input value to the english numbers.
     *
     * @param value
     */
    toEnglish(value) {
        let englishValue = value;
        let regex;
        for (let i = 0; i < PersianNumberService.englishNumbersTable.length; i++) {
            // Language=JSRegexp
            regex = new RegExp(`[${PersianNumberService.persianNumbersTable[i]}]`, 'g');
            englishValue = englishValue.replace(regex, PersianNumberService.englishNumbersTable[i]);
        }
        return englishValue;
    }
}
PersianNumberService.persianNumberPattern = '\u06F0-\u06F9';
PersianNumberService.persianNumbersTable = ['\u06F0', '\u06F1', '\u06F2', '\u06F3', '\u06F4', '\u06F5', '\u06F6', '\u06F7', '\u06F8', '\u06F9'];
PersianNumberService.englishNumbersTable = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
PersianNumberService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: PersianNumberService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
PersianNumberService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: PersianNumberService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: PersianNumberService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * This service brings validation of persian texts to the program and let you convert some not-persian letters with persian ones.
 *
 */
class PersianLetterService {
    /**
     * According to the options values, returns a RegExp object. The regex pattern of it contains regex of finding each of
     * elements those specified in the options object plus persian letters.
     *
     * @return a RegExp object that can use in searching a string for special characters (persian letters and others specified in the options)
     * @param options
     */
    static _stringValidationRegex(options) {
        let result = '';
        if (options.enDigits) {
            result += '0-9';
        }
        if (options.symbols) {
            result += PersianLetterService.SYMBOLS_PATTERN;
        }
        if (options.whitespaces) {
            result += PersianLetterService.WHITESPACES_PATTERN;
        }
        if (options.persianDigits) {
            result += PersianNumberService.persianNumberPattern;
        }
        // Language=JSRegexp
        return new RegExp(`[${result + PersianLetterService.PERSIAN_LETTERS_PATTERN}]`, 'g');
    }
    /**
     * This method gets regex of persian letters and other chars specified in the options and then splits the input value by that regex.
     *
     * If match result is null - there is no match - returns 0. otherwise returns length of the match array that shows number of matched
     * occurred in the input value.
     *
     * @param value
     * @param options
     * @return a number greater equal to 0 representing number of matches occurred in the input value.
     */
    static _getMatchedPatternLength(value, options) {
        const matchResult = value.match(PersianLetterService._stringValidationRegex(options));
        return matchResult ? matchResult.length : 0;
    }
    /**
     * If input value contains any persian letter or is an empty string, returns true. otherwise returns false.
     *
     * @param value
     */
    containsPersian(value) {
        // Language=JSRegexp
        const persianRegex = new RegExp(`[${PersianLetterService.PERSIAN_LETTERS_PATTERN}]`);
        return value.trim() ? persianRegex.test(value) : true;
    }
    /**
     * Checks if input value is a persian text or not. If input value contains any letter that is not persian, the output will be false.
     * Result of validation on existence  of other characters controls by options parameter.
     *
     * @param value
     * @param options controlling validation result on existence  of other characters controls by options parameter. Fields:
     *
     *        persianDigits: if sets to true, validation result will be true if input value contains persian digits plus persian letters.
     *
     *
     *        enDigits: if sets to true, validation result will be true if input value contains english digits plus persian letters
     *
     *
     *        symbols: if sets to true, validation result will be true if input value contains specific symbols plus persian letters.
     *                 symbols list: ?$-/:؟!~"'^_-[]{}()%&*><;
     *
     *
     *        whitespaces: if sets to true, validation result will be true if input value contains white spaces plus persian letters
     *
     *
     * You can combine any number of options field together.
     */
    isPersian(value, options = {
        persianDigits: false,
        enDigits: false,
        symbols: true,
        whitespaces: true
    }) {
        return value.length === PersianLetterService._getMatchedPatternLength(value, options);
    }
    /**
     * Converts arabic letters: ي and ك to persian corresponding letters.
     *
     * @param value
     */
    toPersian(value) {
        return value.replace(/ي/g, 'ی').replace(/ك/g, 'ک');
    }
}
PersianLetterService.PERSIAN_LETTERS_PATTERN = '\u06A9\u06AF\u06C0\u06CC\u060C\u062A\u062B\u062C\u062D\u062E\u062F\u063A\u064A\u064B\u064C' +
    '\u064D\u064E\u064F\u067E\u0670\u0686\u0698\u200C\u0621-\u0629\u0630-\u0639\u0641-\u0654';
PersianLetterService.WHITESPACES_PATTERN = '\\s';
PersianLetterService.SYMBOLS_PATTERN = '؟$-/:-?،{-~!"^_`\\[\\]';
PersianLetterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: PersianLetterService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
PersianLetterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: PersianLetterService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: PersianLetterService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * Reactive form validator that checks form control value contains only persian numbers.
 *
 * @param persianNumberService
 */
function persianNumbersValidator(persianNumberService = new PersianNumberService()) {
    return (control) => persianNumberService.isPersian(control.value) ? null : { persianNumberValidation: control.value };
}
/**
 * Reactive form validator that checks form control value contains only persian letters and allowed characters in the options parameter.
 *
 * @param options see documents of isPersian method of the PersianLetterService for more info about options.
 * @param persianLetterService
 */
function persianLettersValidator(// eslint-disable-line prefer-arrow/prefer-arrow-functions
options = { persianDigits: false, whitespaces: false, symbols: false, enDigits: false }, persianLetterService = new PersianLetterService()) {
    return (control) => persianLetterService.isPersian(control.value, options) ? null : { persianLetterValidation: control.value };
}

/**
 * You can add PersianNumbersDirective property to your input tag for using persianNumbersValidator function on its value.
 *
 * [For more information also see persianNumbersValidator]{@link persianNumbersValidator}
 */
class PersianNumbersDirective {
    /**
     * Only a wrapper for persianNumbersValidator validator function.
     *
     * @param control
     * @return null for success or ValidationError for invalid inputs
     */
    validate(control) {
        return persianNumbersValidator()(control);
    }
}
PersianNumbersDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: PersianNumbersDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
PersianNumbersDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.1.3", type: PersianNumbersDirective, selector: "[ngxPersianNumbersDirective]", providers: [{ provide: NG_VALIDATORS, useExisting: PersianNumbersDirective }] // eslint-disable-line no-use-before-define
    , ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: PersianNumbersDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxPersianNumbersDirective]',
                    providers: [{ provide: NG_VALIDATORS, useExisting: PersianNumbersDirective }] // eslint-disable-line no-use-before-define
                }]
        }] });
/**
 * You can add PersianLetterDirective property to your input tag for using persianNumbersValidator function on its value
 * For setting options value, you can simply set PersianLetterDirective property equal to the PLOptions object.
 * [For more information also see persianLettersValidator]{@link persianLettersValidator}
 */
class PersianLetterDirective {
    /**
     * Only a wrapper for persianLettersValidator validator function.
     *
     * @param control
     * @return null for success or ValidationError for invalid inputs
     */
    validate(control) {
        return persianLettersValidator(this.options)(control);
    }
}
PersianLetterDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: PersianLetterDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
PersianLetterDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.1.3", type: PersianLetterDirective, selector: "[ngxPersianLettersValidator]", inputs: { options: ["PersianLetterDirective", "options"] }, providers: [{ provide: NG_VALIDATORS, useExisting: PersianLetterDirective }] // eslint-disable-line no-use-before-define
    , ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: PersianLetterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxPersianLettersValidator]',
                    providers: [{ provide: NG_VALIDATORS, useExisting: PersianLetterDirective }] // eslint-disable-line no-use-before-define
                }]
        }], propDecorators: { options: [{
                type: Input,
                args: ['PersianLetterDirective']
            }] } });

/**
 * Replaces all arabic and english numbers with persian numbers.
 *
 * @example {{englishTextDigit | faNum}}
 */
class FaNumPipe {
    constructor(persianNumberService) {
        this.persianNumberService = persianNumberService;
    }
    /**
     *
     * @param value a number or string (probably empty).
     * @return a string that all english and arabic numbers in the input has been replaced with persian digits.
     */
    transform(value) {
        const strValue = String(value);
        return this.persianNumberService.toPersian(strValue);
    }
}
FaNumPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: FaNumPipe, deps: [{ token: PersianNumberService }], target: i0.ɵɵFactoryTarget.Pipe });
FaNumPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: FaNumPipe, name: "faNum" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: FaNumPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'faNum' }]
        }], ctorParameters: function () { return [{ type: PersianNumberService }]; } });

/**
 * Replaces all persian numbers in the text to the english numbers.
 *
 * @example {{persianTextDigit | enNum}}
 */
class EnNumPipe {
    constructor(persianNumberService) {
        this.persianNumberService = persianNumberService;
    }
    /**
     * @return input value replaced all persian digits with english digits.
     * @param value a string containing one or more non-english digits. Could be an empty string.
     */
    transform(value) {
        const strValue = String(value);
        return this.persianNumberService.toEnglish(strValue);
    }
}
EnNumPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: EnNumPipe, deps: [{ token: PersianNumberService }], target: i0.ɵɵFactoryTarget.Pipe });
EnNumPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: EnNumPipe, name: "enNum" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: EnNumPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'enNum' }]
        }], ctorParameters: function () { return [{ type: PersianNumberService }]; } });

var IRCurrencies;
(function (IRCurrencies) {
    IRCurrencies["RIAL"] = "\u0631\u06CC\u0627\u0644";
    IRCurrencies["R"] = "\u0631\u06CC\u0627\u0644";
    IRCurrencies["TOMAN"] = "\u062A\u0648\u0645\u0627\u0646";
    IRCurrencies["T"] = "\u062A\u0648\u0645\u0627\u0646";
})(IRCurrencies || (IRCurrencies = {}));
/**
 * Formats input as a currency value.
 * default type is rial. You can choose on of the following currency types:
 *
 *      R or RIAL for ریال
 *
 *      T or TOMAN for تومان
 *
 *  This pipe formats numbers like: 123,456,123
 */
class IRCurrencyPipe {
    /**
     * @param value a number of a string only contains digits
     * @param type currency type
     * @example 1925100 -> 1,925,100 ریال
     */
    // transform(value: number | string, type?: keyof IRCurrencies, digitsInfo?: string): string | null;
    transform(value, type = 'RIAL') {
        const numericValue = Number(value);
        if (isNaN(numericValue)) {
            throw new Error(`NaN is not a acceptable number`);
        }
        return `${numericValue.toLocaleString('en-US')} ${IRCurrencies[type]}`;
    }
}
IRCurrencyPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: IRCurrencyPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
IRCurrencyPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: IRCurrencyPipe, name: "irc" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: IRCurrencyPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'irc' }]
        }] });

/**
 * Formats a 10-digit number or string to national code format.
 */
class NationalCodePipe {
    /**
     * @param value a 10-digit number or a string only contains 10 digits.
     * @example 001-236547-5
     * @return a formatted text from 10-digit input or an empty string if length of the input is not 10.
     */
    transform(value) {
        const normalizedValue = String(value);
        if (normalizedValue.length !== 10) {
            return '';
        }
        return `${normalizedValue.substring(0, 3)}-${normalizedValue.substring(3, 9)}-${normalizedValue[9]}`;
    }
}
NationalCodePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: NationalCodePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
NationalCodePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: NationalCodePipe, name: "nationalCode" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: NationalCodePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'nationalCode' }]
        }] });

/**
 * This function removes fraction part of the number if exists. So, for example, it will turn -145,455 to -145 and
 * 145,455 to 145.
 */
function round(x) {
    if (x < 0) {
        return Math.ceil(x);
    }
    else {
        return Math.floor(x);
    }
}
/**
 * This function returns integer part of division result of num1 and num2 numbers.
 *
 * For example: div (10, 3) is equal to 3.
 */
function div(num1, num2) {
    return round(num1 / num2);
}
/**
 * This function return arithmetic mod result of two inputs as a integer.
 */
function mod(num1, num2) {
    return num1 - (div(num1, num2) * num2);
}

/**
 * This Error will throw When inputted date is not a valid date according to the Jalali Calendar.
 */
class InvalidJalaliDateError extends Error {
    constructor() {
        super('Invalid jalali date.');
    }
}

/**
 * Methods of this Service doing validation stuff on a Jalali date. You can use this Service using DI in all around of your app for
 * validating Jalali dates without needing to convert them to the Georgian Date.
 */
class JalaliDateValidatorService {
    constructor() {
    }
    /**
     * Returns number of days in a given month counting from 1.
     *
     * @param jYear full jalali year like 1397
     * @param jMonth number of month from 0 for Farvardin to 11 for Esfand.
     */
    jMonthLength(jYear, jMonth) {
        if (jMonth < 6) {
            return 31;
        }
        if (jMonth < 11 || this.isJYearLeap(jYear)) {
            return 30;
        }
        return 29;
    }
    /**
     * Calculates the number of the leap years in jalali Calendar.
     *
     * @param targetJYear should be full year like: 1397 and should be between -61 to 3177.
     *
     * [Implementation in source code]{@link https://github.com/sijad/ts-jalaali/blob/296a7c2fa1816a5bbb0b11bbe3eb03ebc17059f6/src/jalaali.ts#L71}
     */
    numOfJLeapYears(targetJYear) {
        // Jalali years starting the 33-year rule.
        const breaks = [
            -61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210,
            1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178
        ];
        const breaksLength = breaks.length;
        let jp = breaks[0];
        let jump = 0;
        let leapJ = -14;
        if (!this.isValidJYear(targetJYear)) {
            throw new InvalidJalaliDateError();
        }
        // Find the limiting years for the Jalali year jy.
        for (let i = 1; i < breaksLength; i += 1) {
            const jm = breaks[i];
            jump = jm - jp;
            if (targetJYear < jm) {
                break;
            }
            leapJ = leapJ + (div(jump, 33) * 8) + div(mod(jump, 33), 4);
            jp = jm;
        }
        const n = targetJYear - jp;
        // Find the number of leap years from AD 621 to the beginning
        // Of the current Jalali year in the Persian Calendar.
        leapJ = leapJ + (div(n, 33) * 8) + div(mod(n, 33) + 3, 4);
        if (mod(jump, 33) === 4 && jump - n === 4) {
            leapJ += 1;
        }
        return leapJ;
    }
    /**
     * If JYear is a leap year in jalali Calendar, return true. We using the fact that if a year is leap, number of leap years until that year
     * and the year after it is not equal.
     *
     * @param jYear full jalali year like: 1397
     */
    isJYearLeap(jYear) {
        return this.numOfJLeapYears(jYear) !== this.numOfJLeapYears(jYear + 1);
    }
    /**
     * Checks if jalali year is in acceptable range or not.
     *
     * [Implementation in source code]{@link https://github.com/sijad/ts-jalaali/blob/296a7c2fa1816a5bbb0b11bbe3eb03ebc17059f6/src/jalaali.ts#L27}
     *
     * @param jYear full jalali year like 1397
     */
    isValidJYear(jYear) {
        return jYear >= JalaliDateValidatorService._minAcceptableYear && jYear <= JalaliDateValidatorService._maxAcceptableYear;
    }
    /**
     * Checks if jalali month is in valid range or not. In default, jMonth should start from zero.
     * If you want to start month number from one instead of zero, you should make startFromZero parameter to false.
     */
    isValidJMonth(jMonth, startFromZero = true) {
        let minMonthNumber = 0;
        let maxMonthNumber = 11;
        if (!startFromZero) {
            minMonthNumber++;
            maxMonthNumber++;
        }
        return jMonth >= minMonthNumber && jMonth <= maxMonthNumber;
    }
    /**
     * Checks if day number is in valid range according to the given year and month.
     *
     * @param jYear full jalali year like 1397
     * @param jMonth month number starting from zero
     * @param jDay day number starting from one
     */
    isValidJDay(jYear, jMonth, jDay) {
        return jDay <= this.jMonthLength(jYear, jMonth) && jDay > 0;
    }
    /**
     * If Jalali date is not a valid Jalali date, return false. otherwise returns true.
     *
     * @param jYear full year number like: 1397
     * @param jMonth starts from zero
     * @param jDay starts from one
     */
    isValidJDate(jYear, jMonth, jDay) {
        return this.isValidJYear(jYear) && this.isValidJMonth(jMonth) && this.isValidJDay(jYear, jMonth, jDay);
    }
}
JalaliDateValidatorService._minAcceptableYear = -61;
JalaliDateValidatorService._maxAcceptableYear = 3177;
JalaliDateValidatorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: JalaliDateValidatorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
JalaliDateValidatorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: JalaliDateValidatorService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: JalaliDateValidatorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/**
 * This class converts Jalali date to Georgian and vase versa. You can use all of the methods as static methods.
 */
class JalaliDateCalculatorService {
    constructor(validator) {
        this.validator = validator;
    }
    /**
     * Calculates the Julian Day number from Gregorian or Julian calendar dates.
     *
     * Only some code cleaning applied to the source code.
     *
     * The procedure was tested to be good since 1 March, -100100 (of both calendars) up to a few million years into the future.
     *
     * @param gDate an instance of javascript date representing a Georgian date.
     *
     * [Implementation in source code]{@link https://github.com/sijad/ts-jalaali/blob/296a7c2fa1816a5bbb0b11bbe3eb03ebc17059f6/src/jalaali.ts#L195}
     */
    numberOfPassedGDays(gDate) {
        const gMonth = gDate.getMonth() - 7;
        const gYear = gDate.getFullYear() + 100100;
        const gMonthDiv = div(gMonth, 6);
        const result = div((gYear + gMonthDiv) * 1461, 4) + div((153 * mod(gMonth + 17, 12)) + 2, 5) + gDate.getDate() - 34840408;
        return result - div(div(gYear + gMonthDiv, 100) * 3, 4) + 752;
    }
    /**
     * Returns number of passed days from source day in jalali calendar to the given jalali date.
     *
     * @param jYear full jalali year like 1397
     * @param jMonth starts from zero
     * @param jDay day number starts from one
     */
    numberOfPassedJDays(jYear, jMonth, jDay) {
        return this.numberOfPassedGDays(new Date(this.jalaliYearToGeorgianYear(jYear), 2, this.firstDayOfJYearInMarch(jYear))) +
            (jMonth * 31) - (div(jMonth + 1, 7) * (jMonth - 6)) + jDay - 1;
    }
    /**
     * Creates a javascript Date object from number of passed days in Georgian calendar representing Georgian date.
     *
     * [Implementation in source code]{@link https://github.com/sijad/ts-jalaali/blob/296a7c2fa1816a5bbb0b11bbe3eb03ebc17059f6/src/jalaali.ts#L213}
     */
    createGDateFromDays(numOfDays) {
        const j = ((4 * numOfDays) + 139361631) + (div(div((4 * numOfDays) + 183187720, 146097) * 3, 4) * 4) - 3908;
        const i = (div(mod(j, 1461), 4) * 5) + 308;
        const gDay = div(mod(i, 153), 5) + 1;
        const gMonth = mod(div(i, 153), 12);
        const gYear = div(j, 1461) - 100100 + div(7 - gMonth, 6);
        return new Date(gYear, gMonth, gDay);
    }
    /**
     * This function returns number of passed leap years from AD 621 until targetGYear.
     *
     * @param targetGYear is full year number like 2018
     *
     * [Implementation in source code]{@link https://github.com/sijad/ts-jalaali/blob/296a7c2fa1816a5bbb0b11bbe3eb03ebc17059f6/src/jalaali.ts#L110}
     */
    numOfGLeapYears(targetGYear) {
        return div(targetGYear, 4) - div((div(targetGYear, 100) + 1) * 3, 4) - 150;
    }
    /**
     * First day of the Farvardin month in Jalali calendar is in March month. This method returns the day number of new jalali year in March.
     * The day number starts from 1 not zero and is equal to the real numbers in the calendar.
     *
     * @param jYear full jalali year like 1397
     */
    firstDayOfJYearInMarch(jYear) {
        const gYear = jYear + 621;
        return this.validator.numOfJLeapYears(jYear) - this.numOfGLeapYears(gYear) + 20;
    }
    /**
     * Converts jalali year number to the georgian year. Output year is the georgian year that jalali year starts within it, not the year
     * that starts in the winter of jalali year.
     *
     * @param jYear full jalali year like 1397
     */
    jalaliYearToGeorgianYear(jYear) {
        return jYear + 621;
    }
    /**
     * Converts georgian year to the jalali year. Output year is the jalali year that start within the Georgian year.
     *
     * @param gYear full georgian year like 2018
     */
    georgianYearToJalaliYear(gYear) {
        return gYear - 621;
    }
    /**
     * This method converts Georgian date to the jalali date. Output is an object implementing SimpleDateInterface.
     *
     * ATTENTION: month number starts from 0, but day number starts from 1. Just like native javascript Date object.
     *
     * @param gDate Georgian date as a javascript Date object.
     *
     * [Implementation in source code]{@link https://github.com/sijad/ts-jalaali/blob/296a7c2fa1816a5bbb0b11bbe3eb03ebc17059f6/src/jalaali.ts#L149}
     */
    convertToJalali(gDate) {
        const georgianYear = gDate.getFullYear();
        let jalaliYear = this.georgianYearToJalaliYear(georgianYear);
        const passedDays = this.numberOfPassedGDays(gDate);
        const numOfPassedDaysTo1Farvardin = this.numberOfPassedGDays(new Date(georgianYear, 2, this.firstDayOfJYearInMarch(jalaliYear)));
        let jalaliDay;
        let jalaliMonth;
        // Find number of days that passed since 1 Farvardin.
        let numOfDayInJYear = passedDays - numOfPassedDaysTo1Farvardin;
        if (numOfDayInJYear >= 0) {
            if (numOfDayInJYear <= 185) {
                // The first 6 months.
                jalaliMonth = div(numOfDayInJYear, 31);
                jalaliDay = mod(numOfDayInJYear, 31) + 1;
                return { year: jalaliYear, month: jalaliMonth, day: jalaliDay };
            }
            else {
                // The remaining months.
                numOfDayInJYear -= 186;
            }
        }
        else {
            // Previous Jalali year.
            jalaliYear -= 1;
            numOfDayInJYear += 179;
            if (this.validator.isJYearLeap(jalaliYear)) {
                numOfDayInJYear += 1;
            }
        }
        jalaliMonth = 6 + div(numOfDayInJYear, 30);
        jalaliDay = mod(numOfDayInJYear, 30) + 1;
        return { year: jalaliYear, month: jalaliMonth, day: jalaliDay };
    }
    /**
     * Converts a valid jalali date to a javascript Date object representing a equivalent Georgian date.
     *
     * @param jYear a full Jalali year like 1397
     * @param jMonth starts from zero
     * @param jDay starts from 1
     * @throws InvalidJalaliDateError when inputted date is not a valid Jalali date.
     */
    convertToGeorgian(jYear, jMonth, jDay) {
        if (!this.validator.isValidJDate(jYear, jMonth, jDay)) {
            throw new InvalidJalaliDateError();
        }
        return this.createGDateFromDays(this.numberOfPassedJDays(jYear, jMonth, jDay));
    }
}
JalaliDateCalculatorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: JalaliDateCalculatorService, deps: [{ token: JalaliDateValidatorService }], target: i0.ɵɵFactoryTarget.Injectable });
JalaliDateCalculatorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: JalaliDateCalculatorService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: JalaliDateCalculatorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: JalaliDateValidatorService }]; } });

/**
 * This class represents a complete Date object for Jalali dates. It accepts jalali Dates, converts Georgian dates to jalali and implements
 * all the behaviours of default Date object of JavaScript for Jalali Date, plus some additional methods for developers convenience.
 *
 * ATTENTION:
 *
 * UTC methods are not implemented for Jalali date. They working directly on gDate object (Corresponding date in Georgian) and changing
 * properties of this. Then new JDate object will create from the modified Georgian Date. So they may Cause unpredictable behaviour.
 * Please don't use UTC methods with "todo" tag on them unless you are sure about the behaviour.
 * Recreating objects are safer than working with UTC methods.
 */
class JDate {
    /**
     * For creating a JDate object, you have 5 different options.
     *
     * 1- If you want to have current date and time, you can simply call new JDate() without any parameter.
     *
     * 2- If you want to create JDate object from a jalali date string as described in the `pars` method document, you can pass that string as
     *    first parameter and leave others empty.
     *
     * 3 - If you want to create JDate object from number of passed milliseconds from UNIX epoch (for example creating a Jalali date object
     *     from result of getTime method of another Date object), you can pass the number as first parameter and leave others alone.
     *
     * 4 - If you want to create JDate object from a Georgian Date object, you can simply pass that Date object as first parameter and leave
     *     others empty.
     *
     * 5- If you want to create JDate object from date and time values, you can simply fill corresponding parameters of each date and time
     * value to the constructor. You don't have to fill all of the parameters. only those you need. other parameters will fill with zero.
     * Examples of all of those scenarios:
     *
     * @example new JDate()
     * @example new JDate('11 دی 1348 00:00:00')
     * @example new JDate(-12600000)
     * @example new JDate(new Date(2018, 0, 1))
     * @example new JDate(1397, 0, 25)
     * @example new JDate(1397, 11, 25, 12, 32, 45, 123)
     * @param jYear jalai
     * @param jMonth Month number starting from 0 and should be LESSER than 12.
     * @param jDay
     * @param hours
     * @param minutes
     * @param seconds
     * @param milliseconds
     * @param timezone String name of desired timezone. The default value is Tehran.
     * @throws InvalidJalaliDateError
     */
    constructor(jYear, jMonth, jDay, hours = 0, minutes = 0, seconds = 0, milliseconds = 0, timezone = null) {
        this._calculator = new JalaliDateCalculatorService(new JalaliDateValidatorService());
        this._timezone = timezone;
        if (!jYear) {
            this._createFromDate(JDate.changeTimeZone(new Date(), timezone));
        }
        else if (typeof jYear === 'string' && jMonth === undefined) {
            this._createFromDate(JDate.changeTimeZone(new Date(JDate.parse(jYear)), timezone));
        }
        else if (typeof jYear === 'number' && jMonth === undefined) {
            this._createFromDate(JDate.changeTimeZone(new Date(jYear), timezone));
        }
        else if (jYear instanceof Date && jMonth === undefined) {
            this._createFromDate(JDate.changeTimeZone(jYear, timezone));
        }
        else {
            const gregorianDate = this._calculator.convertToGeorgian(jYear, jMonth, jDay);
            gregorianDate.setHours(hours, minutes, seconds, milliseconds);
            this._createFromDate(JDate.changeTimeZone(gregorianDate, timezone));
        }
        this._checkDateIsValid();
    }
    /**
     * Sets Jalali year value to the input parameter and recalculates gDate attribute.
     *
     * @param value full Jalali year like 1397
     */
    set jalaliYear(value) {
        this._jalaliYear = value;
        this._checkDateIsValid();
        this._renewGDate();
    }
    /**
     * Sets Jalali month value to the input parameter and recalculates gDate attribute.
     *
     * @param value month number starting from zero
     */
    set jMonth(value) {
        this._jMonth = value;
        this._checkDateIsValid();
        this._renewGDate();
    }
    /**
     * Sets Jalali day value to the input parameter and recalculates gDate attribute.
     *
     * @param value day number starting from 1.
     */
    set jDay(value) {
        this._jDay = value;
        this._checkDateIsValid();
        this._renewGDate();
    }
    /**
     * If input value length is shorter than desiredLength, adds zeros at the beginning of it until meets desired length.
     *
     * @param value a number or string that we want have a specific length
     * @param desiredLength length of output string. If be shorter than input length, input will return.
     */
    static zeroPadding(value, desiredLength) {
        let strValue = value.toString();
        while (strValue.length < desiredLength) {
            strValue = '0' + strValue;
        }
        return strValue;
    }
    /**
     * Extracts Georgian Date object from a Jalali date string.
     *
     * @param dateString a Jalali date string following this pattern:
     *
     *        "yyyy mmm dd HH:MM:SS"
     *        or this pattern:
     *
     *        "yyyy mmmm dd HH:MM:SS".
     * @example 11 دی 1348 00:00:00
     * @example 11 Dey 1348 00:00:00
     * @return a Georgian Date object.
     */
    static parse(dateString) {
        const calculator = new JalaliDateCalculatorService(new JalaliDateValidatorService());
        const dateArray = dateString.split(' ');
        if (dateArray.length < 3) {
            throw new InvalidJalaliDateError();
        }
        const day = parseInt(dateArray[0], 10);
        let month = JDate.FA_MONTHS.indexOf(dateArray[1]);
        if (month === -1) {
            month = JDate.EN_MONTHS.indexOf(dateArray[1]);
        }
        if (month === -1) {
            throw new InvalidJalaliDateError();
        }
        const year = parseInt(dateArray[2], 10);
        const timeArray = dateArray.length > 3 ? dateArray[3].split(':') : ['0', '0', '0'];
        const hour = parseInt(timeArray[0], 10);
        const min = parseInt(timeArray[1], 10);
        const sec = parseInt(timeArray[2], 10);
        const gDate = calculator.convertToGeorgian(year, month, day);
        gDate.setHours(hour, min, sec);
        return gDate.getTime();
    }
    /**
     * Converts given date to a specific timezone.
     * If timezone is null, the original date object will return.
     *
     * @param date The date object we want to get its value to another timezone.
     * @param timeZone Name of target timezone.
     *
     * @return a Georgian Date object in new timezone.
     */
    static changeTimeZone(date, timeZone) {
        if (!timeZone) {
            return date;
        }
        const localDate = new Date(date.toLocaleString('en-US', { timeZone }));
        localDate.setSeconds(date.getSeconds(), date.getMilliseconds()); // The above line replaces these values to zero.
        const timeDifference = date.getTime() - localDate.getTime();
        return new Date(date.getTime() - timeDifference);
    }
    toLocaleString(locales, options) {
        throw new Error('Method not implemented.');
    }
    [Symbol.toPrimitive](hint) {
        return undefined;
    }
    /**
     * @return a regular javascript Date object representing Georgian date corresponding to the Jalili date of the JDate object.
     */
    getGeorgianDate() {
        return this._gregorianDate;
    }
    /**
     * @return the day of the month for the specified date according to local time.
     */
    getDate() {
        return this._jDay;
    }
    /**
     *  @return the day of the week for the specified date according to local time, where 0 represents Friday and 6 is Thursday.
     */
    getDay() {
        if (this._gregorianDate === undefined) {
            throw InvalidJalaliDateError;
        }
        return (this._gregorianDate.getDay() + 2) % 7;
    }
    /**
     * @return the year (4 digits for years greater than 999) of the specified date according to local time
     * @example 1397
     * @example 100
     */
    getFullYear() {
        if (!this._jalaliYear) {
            throw InvalidJalaliDateError;
        }
        return this._jalaliYear;
    }
    /**
     * @return the hour for the specified date, according to local time.
     * @example 10
     */
    getHours() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.getHours();
    }
    /**
     * Converts default 24-hour clock hour value to the 12-hour clock version.
     *
     * @return a number between 1 to 12
     */
    getHours12hourClock() {
        const result = this.getHours() % 12;
        return result ? result : 12;
    }
    /**
     * @return the milliseconds in the specified date according to local time.
     */
    getMilliseconds() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.getMilliseconds();
    }
    /**
     * @Return the minutes in the specified date according to local time.
     */
    getMinutes() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.getMinutes();
    }
    /**
     * @return the month in the specified date according to local time, as a zero-based value
     * where zero indicates the first month of the year.
     */
    getMonth() {
        if (this._jMonth === undefined) {
            throw InvalidJalaliDateError;
        }
        return this._jMonth;
    }
    /**
     * @return the seconds in the specified date according to local time.
     */
    getSeconds() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.getSeconds();
    }
    /**
     * JavaScript uses milliseconds as the unit of measurement, whereas Unix Time is in seconds.
     *
     * getTime() always uses UTC for time representation. For example, a client browser in one timezone, getTime() will be the same as a
     * client browser in any other timezone.
     *
     *You can use this method to help assign a date and time to another Date object. This method is functionally equivalent to the
     *  valueOf() method.
     *
     * @return the number of milliseconds since the Unix Epoch.
     */
    getTime() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.getTime();
    }
    getUTCTime() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return (this._gregorianDate.getTime() + (this._gregorianDate.getTimezoneOffset() * 60 * 1000));
    }
    /**
     * Attention: Not implemented
     *
     * @return the time zone difference, in minutes, from current locale (host system settings) to UTC
     * @todo add implementation
     */
    getTimezoneOffset() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.getTimezoneOffset();
    }
    /**
     * Output is not jalali day.
     *
     * @return getUTCDate of the corresponding Georgian date.
     * @todo add implementation
     */
    getUTCDate() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.getUTCDate();
    }
    /**
     * Output is not jalali day.
     *
     * @return getUTCDay of the corresponding Georgian date.
     * @todo add implementation
     */
    getUTCDay() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.getUTCDay();
    }
    /**
     * Output is not a Jalali Year.
     *
     * @return getUTCFullYear of the corresponding Georgian date.
     * @todo add implementation
     */
    getUTCFullYear() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.getUTCFullYear();
    }
    /**
     * @return getUTCHours of the corresponding Georgian date.
     * @todo add implementation
     */
    getUTCHours() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.getUTCHours();
    }
    /**
     * @return getUTCMilliseconds of the corresponding Georgian date.
     * @todo add implementation
     */
    getUTCMilliseconds() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.getUTCMilliseconds();
    }
    /**
     * @return getUTCMinutes of the corresponding Georgian date.
     * @todo add implementation
     */
    getUTCMinutes() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.getUTCMinutes();
    }
    /**
     * Output is not a Jalali Year.
     *
     * @return getUTCMonth of the corresponding Georgian date.
     * @todo add implementation
     */
    getUTCMonth() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.getUTCMonth();
    }
    /**
     * @return getUTCSeconds of the corresponding Georgian date.
     * @todo add implementation
     */
    getUTCSeconds() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.getUTCSeconds();
    }
    /**
     * Sets the day of the JDate object relative to the beginning of the currently set month.
     *
     * @param date day number starts from 1.
     * @return The number of milliseconds between 1 January 1970 00:00:00 UTC and the given date (the Date object is also changed in place).
     */
    setDate(date) {
        this.jDay = date;
        return this.getTime();
    }
    /**
     * Sets the full year for a specified date according to local time. Returns new timestamp.
     *
     * @param year full Jalali year like 1397
     * @param month number of month starting from 0
     * @param date number of day starting from 1
     */
    setFullYear(year, month, date) {
        this.jalaliYear = year;
        if (month) {
            this.jMonth = month;
        }
        if (date) {
            this.jDay = date;
        }
        return this.getTime();
    }
    /**
     * Sets the hours for a specified date according to local time, and returns the number of milliseconds since
     * January 1, 1970 00:00:00 UTC until the time represented by the updated JDate instance.
     *
     * @param hours An integer between 0 and 23, representing the hour
     * @param min An integer between 0 and 59, representing the minutes.
     * @param sec An integer between 0 and 59, representing the seconds.
     * @param ms A number between 0 and 999, representing the milliseconds.
     * @return The number of milliseconds between January 1, 1970 00:00:00 UTC and the updated date.
     */
    setHours(hours, min, sec, ms) {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        this._gregorianDate.setHours(hours);
        if (min !== undefined) {
            this.setMinutes(min);
        }
        if (sec !== undefined) {
            this.setSeconds(sec);
        }
        if (ms !== undefined) {
            this.setMilliseconds(ms);
        }
        return this.getTime();
    }
    /**
     * Sets the milliseconds for a specified date according to local time.
     *
     * @param ms A number between 0 and 999, representing the milliseconds.
     * @return The number of milliseconds between 1 January 1970 00:00:00 UTC and the updated date.
     */
    setMilliseconds(ms) {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        this._gregorianDate.setMilliseconds(ms);
        return this.getTime();
    }
    /**
     * Sets the minutes for a specified date according to local time.
     *
     * @param min An integer between 0 and 59, representing the minutes.
     * @param sec An integer between 0 and 59, representing the seconds.
     * @param ms A number between 0 and 999, representing the milliseconds.
     * @return The number of milliseconds between 1 January 1970 00:00:00 UTC and the updated date.
     */
    setMinutes(min, sec, ms) {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        this._gregorianDate.setMinutes(min);
        if (sec !== undefined) {
            this.setSeconds(sec);
        }
        if (ms !== undefined) {
            this.setMilliseconds(ms);
        }
        return this.getTime();
    }
    /**
     * Sets the month for a specified date according to the currently set year.
     *
     * @param month An integer between 0 and 11, representing the months Farvardin through Esfand.
     * @param date An integer from 1 to 31, representing the day of the month.
     * @return The number of milliseconds between 1 January 1970 00:00:00 UTC and the updated date.
     */
    setMonth(month, date) {
        this.jMonth = month;
        if (date !== undefined) {
            this.jDay = date;
        }
        return this.getTime();
    }
    /**
     * Sets the seconds for a specified date according to local time.
     *
     * @param sec An integer between 0 and 59, representing the seconds.
     * @param ms A number between 0 and 999, representing the milliseconds.
     * @return The number of milliseconds between 1 January 1970 00:00:00 UTC and the updated date.
     */
    setSeconds(sec, ms) {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        this._gregorianDate.setSeconds(sec);
        if (ms !== undefined) {
            this.setMilliseconds(ms);
        }
        return this.getTime();
    }
    /**
     * Sets the JDate object to the time represented by a number of milliseconds since January 1, 1970, 00:00:00 UTC.
     *
     * @param time sets the Date object to the time represented by a number of milliseconds since January 1, 1970, 00:00:00 UTC.
     * @return The number of milliseconds between 1 January 1970 00:00:00 UTC and the updated date.
     */
    setTime(time) {
        this._createFromDate(new Date(time));
        return time;
    }
    /**
     * Sets the day of the month for a specified date according to universal time.
     * Then recreate the JDate object from new Georgian object.
     *
     * @param  date An integer from 1 to 31, representing the day of the month.
     * @return The number of milliseconds between 1 January 1970 00:00:00 UTC and the updated date.
     * @todo add implementation
     */
    setUTCDate(date) {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        this._gregorianDate.setUTCDate(date);
        this._createFromDate(this._gregorianDate);
        return this.getTime();
    }
    /**
     * Sets the full year for a specified date according to universal time.
     * Then recreate the JDate object from new Georgian object.
     *
     * @param year  An integer specifying the numeric value of the year, for example, 1995.
     * @param month  Optional. An integer between 0 and 11 representing the months January through December.
     * @param date An integer between 1 and 31 representing the day of the month. If you specify the dayValue parameter, you must also
     * specify the monthValue.
     * @todo add implementation
     */
    setUTCFullYear(year, month, date) {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        this._gregorianDate.setUTCFullYear(year, month, date);
        this._createFromDate(this._gregorianDate);
        return this.getTime();
    }
    /**
     * Sets the hour for a specified date according to universal time, and returns the number of milliseconds since
     * January 1, 1970 00:00:00 UTC until the time represented by the updated Date instance.
     * Then recreate the JDate object from new Georgian object.
     *
     * @param hours  An integer between 0 and 23, representing the hour.
     * @param min Optional. An integer between 0 and 59, representing the minutes.
     * @param sec Optional. An integer between 0 and 59, representing the seconds. If you specify the secondsValue parameter,
     *        you must also specify the minutesValue.
     * @param ms Optional. A number between 0 and 999, representing the milliseconds. If you specify the msValue parameter,
     *        you must also specify the minutesValue and secondsValue.
     * @return The number of milliseconds between January 1, 1970 00:00:00 UTC and the updated date.
     * @todo add implementation
     */
    setUTCHours(hours, min, sec, ms) {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        this._gregorianDate.setUTCHours(hours, min, sec, ms);
        this._createFromDate(this._gregorianDate);
        return this.getTime();
    }
    /**
     * Sets the milliseconds for a specified date according to universal time.
     *
     * Then recreate the JDate object from new Georgian object.
     *
     * @param ms A number between 0 and 999, representing the milliseconds.
     * @return The number of milliseconds between 1 January 1970 00:00:00 UTC and the updated date.
     * @todo add implementation
     */
    setUTCMilliseconds(ms) {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        this._gregorianDate.setUTCMilliseconds(ms);
        this._createFromDate(this._gregorianDate);
        return this.getTime();
    }
    /**
     * Sets the minutes for a specified date according to universal time.
     *
     * Then recreate the JDate object from new Georgian object.
     *
     * @param min An integer between 0 and 59, representing the minutes.
     * @param sec Optional. An integer between 0 and 59, representing the seconds. If you specify the secondsValue parameter,
     *        you must also specify the minutesValue.
     * @param ms Optional. A number between 0 and 999, representing the milliseconds. If you specify the msValue parameter,
     *        you must also specify the minutesValue and secondsValue.
     * @return The number of milliseconds between 1 January 1970 00:00:00 UTC and the updated date.
     * @todo add implementation
     */
    setUTCMinutes(min, sec, ms) {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        this._gregorianDate.setUTCMinutes(min, sec, ms);
        this._createFromDate(this._gregorianDate);
        return this.getTime();
    }
    /**
     * Sets the month for a specified date according to universal time.
     *
     * Then recreate the JDate object from new Georgian object.
     *
     * @param month An integer between 0 and 11, representing the months January through December.
     * @param date Optional. An integer from 1 to 31, representing the day of the month.
     * @return The number of milliseconds between 1 January 1970 00:00:00 UTC and the updated date.
     * @todo add implementation
     */
    setUTCMonth(month, date) {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        this._gregorianDate.setUTCMonth(month, date);
        this._createFromDate(this._gregorianDate);
        return this.getTime();
    }
    /**
     * Sets the seconds for a specified date according to universal time.
     *
     * Then recreate the JDate object from new Georgian object.
     *
     * @param sec An integer between 0 and 59, representing the seconds.
     * @param ms Optional. A number between 0 and 999, representing the milliseconds.
     * @todo add implementation
     */
    setUTCSeconds(sec, ms) {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        this._gregorianDate.setUTCSeconds(sec, ms);
        this._createFromDate(this._gregorianDate);
        return this.getTime();
    }
    /**
     * @return name of the day of the week in persian.
     * @example دوشنبه
     */
    getNameOfTheDay() {
        return JDate.DAYS_OF_WEEK[this.getDay()];
    }
    /**
     * @return name of the month in persian.
     * @example مهر
     */
    getNameOfTheMonth() {
        return JDate.FA_MONTHS[this.getMonth()];
    }
    /**
     * Returns the date portion of a Date object in human readable form in Persian.
     *
     * @return a string following this pattern: "nameOfTheDay nameOfTheMonth dayNumber fullYear".
     * @example پنج‌شنبه اسفند 30 1375
     */
    toDateString() {
        return `${this.getNameOfTheDay()} ${this.getNameOfTheMonth()} ${this.getDate()} ${this.getFullYear()}`;
    }
    /**
     * Returns time marker of object time. all hour numbers lesser than 12 are before noon and all greater than 12 are after noon.
     *
     * @param shortVersion controls output. if be true, output will be in short format like: ب.ظ if be false, output will be in complete
     *        format like: بعد از ظهر
     * @return time marker for showing if time is before noon or after it
     */
    getTimeMarker(shortVersion = false) {
        if (this.getHours() < 12) {
            return shortVersion ? JDate.SHORT_BEFORE_NOON : JDate.COMPLETE_BEFORE_NOON;
        }
        return shortVersion ? JDate.SHORT_AFTER_NOON : JDate.COMPLETE_AFTER_NOON;
    }
    /**
     * This method format date and time stored in the JDate object according to the entered pattern.
     *
     * Only masks will replace and all other characters will be unchanged after formatting.
     *
     * You can use masks several times in a pattern but be careful because if some of masks written immediately, they create new masks with
     * different meaning. It's better to always have some splitter characters between different masks.
     *
     * @param pattern a string containing zero or more formatting mask.
     *
     * Masks:
     *
     *        yyyy -> Year number in 4-digit representation. Leading zero for years lesser than 1000 ex: 1397
     *
     *        yy -> Year number in 2-digit representation without Leading zeros. ex: 97
     *
     *        mmmm -> Name of the month in English representation. ex: Esfand
     *
     *        mmm -> Name of the month in Persian representation. ex: اسفند
     *
     *        mm -> 2-digit number of the month starting from 1. Leading zero for single-digit months.
     *
     *        m -> number of the month starting from 1 without Leading zeros.
     *
     *        dddd -> Name of the day in the week in English representation. ex: Shanbe
     *
     *        ddd -> Name of the day in the week id Persian representation. ex: شنبه
     *
     *        dd -> 2-digit number of the day in the month starting from 1. Leading zero for single-digit days.
     *
     *        d -> number of the day in the month starting from 1 without Leading zeros.
     *
     *        HH -> 2-digit form of hour number in 24-hour clock format. Leading zero for single-digit hours.
     *
     *        H -> non zero-padding form of hour number in 24-hour clock format without Leading zeros.
     *
     *        hh -> 2-digit form of hour number in 12-hour clock format. Leading zero for single-digit hours.
     *
     *        H -> non zero-padding form of hour number in 12-hour clock format without Leading zeros.
     *
     *        MM -> 2-digit form of minutes number. Leading zero for single-digit minutes.
     *
     *        M -> non zero-padding form of minutes number without Leading zeros.
     *
     *        SS -> 2-digit form of seconds number. Leading zero for single-digit seconds.
     *
     *        S -> non zero-padding form of seconds number without Leading zeros.
     *
     *        l -> number of milliseconds without Leading zeros.
     *
     *        T -> Time marker in full format like: قبل از ظهر
     *
     *        t -> Time marker in short format like: ق.ظ
     *@return formatted dateTime string.
     */
    format(pattern) {
        return this._fromTime(this._fromDate(pattern));
    }
    /**
     * @return a string in simplified extended ISO format (ISO 8601), which is always 24 or 27 characters long (yyyy-mm-ddTHH:MM:SS.lZ).
     *        Be careful because that T in the middle of the pattern is not a format Mask and is a simple character.
     */
    toISOString() {
        return this.format('yyyy-mm-dd') + 'T' + this.format('HH:MM:SS.l') + 'Z';
    }
    /**
     * @return a string representation of the Date object.
     * [see toString method]{@link toString}
     */
    toJSON(_) {
        return this.toString();
    }
    /**
     * Returns formatted date with following pattern: 'ddd mmm d yyyy HH:MM:SS'
     */
    toString() {
        return this.format('ddd mmm d yyyy HH:MM:SS');
    }
    toLocaleDateString(locales, options) {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.toLocaleDateString(locales, options);
    }
    toLocaleTimeString(locales, options) {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.toLocaleTimeString(locales, options);
    }
    /**
     * @return toTimeString of Georgian date
     *
     * [For more information see javascript Date object documentation about this
     * method]{@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toTimeString}
     */
    toTimeString() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.toTimeString();
    }
    /**
     * @return toUTCString of Georgian date.
     *
     * [For more information see javascript Date object documentation about this
     * method]{@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toUTCString}
     * @todo add implementation
     */
    toUTCString() {
        if (!this._gregorianDate) {
            throw InvalidJalaliDateError;
        }
        return this._gregorianDate.toUTCString();
    }
    /**
     * Similar to the getTime method.
     *
     * [For more information also see getTime method]{@link getTime}
     */
    valueOf() {
        return this.getTime();
    }
    /**
     * This method recalculates the gDate value with private attributes those storing Jalali date parts.
     */
    _renewGDate() {
        if (!this._jalaliYear || !this._jMonth || !this._jDay) {
            throw InvalidJalaliDateError;
        }
        this._gregorianDate = this._calculator.convertToGeorgian(this._jalaliYear, this._jMonth, this._jDay);
    }
    /**
     * Throws InvalidJalaliDateError when date values of this object won't represent a valid Jalali date.
     * Otherwise nothing happens.
     *
     * @throws InvalidJalaliDateError
     */
    _checkDateIsValid() {
        if (this._jalaliYear === undefined || this._jMonth === undefined || this._jDay === undefined) {
            throw new InvalidJalaliDateError();
        }
        if (!this._calculator.validator.isValidJDate(this._jalaliYear, this._jMonth, this._jDay)) {
            throw new InvalidJalaliDateError();
        }
    }
    /**
     * Calculates Jalali year from Georgian Date object and sets the attributes of the object to proper values.
     *
     * @param gDate: The default JS Date object.
     */
    _createFromDate(gDate) {
        const conversionResult = this._calculator.convertToJalali(gDate);
        this._jalaliYear = conversionResult.year;
        this._jMonth = conversionResult.month;
        this._jDay = conversionResult.day;
        this._gregorianDate = gDate;
    }
    /**
     * Replace patterns of date formatting with corresponding strings from JDate object values.
     * In bi-character pattern parts, missed digits will fill with zero.
     *
     * @param pattern a pattern string with replaceable parts:
     *
     *        yyyy -> Year number in 4-digit representation. ex: 1397
     *
     *        yy -> Year number in 2-digit representation. ex: 97
     *
     *        mmmm -> Name of the month in English representation. ex: Esfand
     *
     *        mmm -> Name of the month in Persian representation. ex: اسفند
     *
     *        mm -> 2-digit number of the month starting from 1
     *
     *        m -> non zero-padding number of the month starting from 1
     *
     *        dddd -> Name of the day in the week in English representation. ex: Shanbe
     *
     *        ddd -> Name of the day in the week id Persian representation. ex: شنبه
     *
     *        dd -> 2-digit number of the day in the month starting from 1
     *
     *        d -> non zero-padding number of the day in the month starting from 1
     *
     * @return A formatted string that all Date pattern parts has been replaced. Other characters of the pattern will left unchanged.
     */
    _fromDate(pattern) {
        return pattern.replace(/yyyy/g, JDate.zeroPadding(this.getFullYear(), 4)).
            replace(/\byy\b/g, (this.getFullYear() % 100).toString()).
            replace(/\bmmmm\b/g, JDate.EN_MONTHS[this.getMonth()]).
            replace(/\bmmm\b/g, JDate.FA_MONTHS[this.getMonth()]).
            replace(/\bmm\b/g, JDate.zeroPadding(this.getMonth() + 1, 2)).
            replace(/\bm\b/g, (this.getMonth() + 1).toString()).
            replace(/\bdddd\b/g, JDate.EN_DAYS_OF_WEEK[this.getDay()]).
            replace(/\bddd\b/g, JDate.DAYS_OF_WEEK[this.getDay()]).
            replace(/\bdd\b/g, JDate.zeroPadding(this.getDate(), 2)).
            replace(/\bd\b/g, this.getDate().toString());
    }
    /**
     * Replace patterns of time formatting with corresponding strings from JDate object values.
     *
     * In bi-character pattern parts, missed digits will fill with zero.
     *
     * @param pattern a pattern string with replaceable parts:
     *
     *        HH -> 2-digit form of hour number in 24-hour clock format.
     *
     *        H -> non zero-padding form of hour number in 24-hour clock format.
     *
     *        hh -> 2-digit form of hour number in 12-hour clock format.
     *
     *        h -> non zero-padding form of hour number in 12-hour clock format.
     *
     *        H -> non zero-padding form of hour number in 12-hour clock format.
     *
     *        MM -> 2-digit form of minutes number.
     *
     *        M -> non zero-padding form of minutes number
     *
     *        SS -> 2-digit form of seconds number.
     *
     *        S -> non zero-padding form of seconds number.
     *
     *        l -> number of milliseconds
     *
     *        T -> Time marker in full format like: قبل از ظهر
     *
     *        t -> Time marker in short format like: ق.ظ
     */
    _fromTime(pattern) {
        return pattern.replace(/\bHH\b/g, JDate.zeroPadding(this.getHours(), 2)).
            replace(/\bH\b/g, this.getHours().toString()).
            replace(/\bhh\b/g, JDate.zeroPadding(this.getHours12hourClock(), 2)).
            replace(/\bh\b/g, this.getHours12hourClock().toString()).
            replace(/\bMM\b/g, JDate.zeroPadding(this.getMinutes(), 2)).
            replace(/\bM\b/g, this.getMinutes().toString()).
            replace(/\bSS\b/g, JDate.zeroPadding(this.getSeconds(), 2)).
            replace(/\bS\b/g, this.getSeconds().toString()).
            replace(/\bl\b/g, this.getMilliseconds().toString()).
            replace(/\bT\b/g, this.getTimeMarker(false)).
            replace(/\bt\b/g, this.getTimeMarker(true));
    }
}
JDate.EN_MONTHS = ['Farvardin', 'Ordibehesht', 'Khordad', 'Tir', 'Mordad', 'Shahrivar', 'Mehr', 'Aban', 'Azar', 'Dey', 'Behman', 'Esfand'];
JDate.FA_MONTHS = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];
JDate.DAYS_OF_WEEK = ['جمعه', 'شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه'];
JDate.EN_DAYS_OF_WEEK = ['Jom\'e', 'Shanbe', 'Yekshanbe', 'Doshanbe', 'Seshanbe', 'Cheharshanbe', 'Panjshanbe'];
JDate.COMPLETE_BEFORE_NOON = 'قبل از ظهر';
JDate.COMPLETE_AFTER_NOON = 'بعد از ظهر';
JDate.SHORT_BEFORE_NOON = 'ق.ظ';
JDate.SHORT_AFTER_NOON = 'ب.ظ';

/**
 * Pre-defined formats date formats those can use for formatting JDate objects with jdate pipe.
 */
var JDateFormat;
(function (JDateFormat) {
    JDateFormat["SHORT"] = "yy/m/d h:M t";
    JDateFormat["SHORT_DATE"] = "yy/m/d h:M t";
    JDateFormat["MEDIUM"] = "d mmm yyyy\u060C h:M t";
    JDateFormat["MEDIUM_DATE"] = "d mmm yyyy\u060C h:M t";
    JDateFormat["LONG"] = "d mmm yyyy\u060C h:M:S T";
    JDateFormat["LONG_DATE"] = "d mmm yyyy\u060C h:M:S T";
    JDateFormat["SHORT_TIME"] = "h:M t";
    JDateFormat["MEDIUM_TIME"] = "h:M:S t";
    JDateFormat["LONG_TIME"] = "h:M:S.l T";
})(JDateFormat || (JDateFormat = {}));
/**
 * Formats JDate objects or convert Date objects (or any instance of classes those implement that interface) to a `JDate` object and format
 * that according to the given format.
 *
 * The result of this pipe is not reevaluated when the input is mutated. To avoid the need to reformat the date on every change-detection
 * cycle, treat the date as an immutable object and change the reference when the pipe needs to run again.
 *
 * You can pass a format pattern string like [JDate format method]{@link https://alihoseiny.github.io/ngx-persian/classes/JDate.html#format}
 *
 * Also you can use pre-defined format names described below:
 *
 *        'SHORT': equivalent to yy/m/d h:M t {@example '97/2/23 12:12 ب.ظ'}
 *
 *        'SHORT_DATE': same as short
 *
 *        'MEDIUM': equivalent to 'd mmm yyyy، h:M t' {@example '23 اردیبهشت 1397، 12:12 ب.ظ'}
 *
 *        'MEDIUM_DATE': same as medium
 *
 *        'LONG': equivalent to 'd mmm yyyy، h:M:S T' {@example '23 اردیبهشت 1397، 12:12:30 بعد از ظهر'}
 *
 *        'LONG_DATE': same as long
 *
 *        'SHORT_TIME': equivalent to 'h:M t' {@example '12:12 ب.ظ'}
 *
 *        'MEDIUM_TIME': equivalent to 'h:M:S t' {@example '12:12:30 ب.ظ'}
 *
 *        'LONG_TIME': equivalent to 'h:M:S.l T' {@example '12:12:30.300 بعد از ظهر'}
 *
 *  Default format is `medium`.
 */
class JdatePipe {
    /**
     * Checks if `formatName` is in the `FormatNames` enum. If it is, returns related format string. Else returns `formatName` string
     * without any change.
     *
     * @param formatName format name or format pattern string
     * @return formatting pattern string
     */
    static convertNameToFormat(formatName) {
        if (!JDateFormat[formatName]) {
            return formatName;
        }
        return JDateFormat[formatName];
    }
    /**
     * Formatting the date using `format` method of it with proper formatting pattern created from `format`
     *
     * @param value a Jalali Date object or an instance of `Date` object or any class implementing that interface
     * @param format format name or format pattern string
     */
    transform(value, format = 'MEDIUM_DATE') {
        let inputValue = value;
        if (!(inputValue instanceof JDate)) {
            inputValue = new JDate(inputValue);
        }
        // @ts-ignore
        return inputValue.format(JdatePipe.convertNameToFormat(format));
    }
}
JdatePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: JdatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
JdatePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: JdatePipe, name: "jdate" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: JdatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'jdate'
                }]
        }] });

class NgxPersianModule {
}
NgxPersianModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: NgxPersianModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgxPersianModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: NgxPersianModule, declarations: [PersianLetterDirective,
        PersianNumbersDirective,
        FaNumPipe,
        EnNumPipe,
        IRCurrencyPipe,
        NationalCodePipe,
        JdatePipe], exports: [PersianLetterDirective,
        PersianNumbersDirective,
        FaNumPipe,
        EnNumPipe,
        IRCurrencyPipe,
        NationalCodePipe,
        JdatePipe] });
NgxPersianModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: NgxPersianModule, imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: NgxPersianModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        PersianLetterDirective,
                        PersianNumbersDirective,
                        FaNumPipe,
                        EnNumPipe,
                        IRCurrencyPipe,
                        NationalCodePipe,
                        JdatePipe
                    ],
                    imports: [],
                    exports: [
                        PersianLetterDirective,
                        PersianNumbersDirective,
                        FaNumPipe,
                        EnNumPipe,
                        IRCurrencyPipe,
                        NationalCodePipe,
                        JdatePipe
                    ]
                }]
        }] });

/**
 * When mobile phone number is not valid this error will throw.
 */
class InvalidMobilePhoneNumberError extends Error {
    constructor() {
        super('This phone number is not valid.');
    }
}

/**
 * This error will throw when there is no valid mobile operator for inputted mobile phone number.
 */
class InvalidMobileOperatorError extends Error {
    constructor() {
        super('Operator of this number is not recognizable.');
    }
}

/**
 * English name of mobile operators are enum keys and persian name of them are values.
 * All characters of all of the keys are in lowercase
 */
var operatorsNames;
(function (operatorsNames) {
    operatorsNames["IRANCELL"] = "\u0627\u06CC\u0631\u0627\u0646\u0633\u0644";
    operatorsNames["MCI"] = "\u0647\u0645\u0631\u0627\u0647 \u0627\u0648\u0644";
    operatorsNames["TALIA"] = "\u062A\u0627\u0644\u06CC\u0627";
    operatorsNames["RIGHTEL"] = "\u0631\u0627\u06CC\u062A\u0644";
    operatorsNames["SPADAN"] = "\u0627\u0633\u067E\u0627\u062F\u0627\u0646";
    operatorsNames["TKC"] = "\u0634\u0628\u06A9\u0647 \u0645\u0633\u062A\u0642\u0644 \u062A\u0644\u0641\u0646 \u0647\u0645\u0631\u0627\u0647 \u06A9\u06CC\u0634";
    operatorsNames["SHATEL"] = "\u0634\u0627\u062A\u0644";
    operatorsNames["APTEL"] = "\u0622\u067E\u062A\u0644";
    operatorsNames["AZARTEL"] = "\u0622\u0630\u0631\u062A\u0644";
    operatorsNames["SAMANTEL"] = "\u0633\u0627\u0645\u0627\u0646\u062A\u0644";
    operatorsNames["LOTUSTEL"] = "\u0644\u0648\u062A\u0648\u0633\u200C\u062A\u0644";
    operatorsNames["ANARESTAN"] = "\u0627\u0646\u0627\u0631\u0633\u062A\u0627\u0646";
})(operatorsNames || (operatorsNames = {}));
/**
 * A constant object. Keys are Mobile operator names and values are arrays those keep mobile codes.
 */
const MobileCodes = {
    irancell: ['0901', '0902', '0903', '0904', '0905', '0930', '0933', '0935', '0936', '0937', '0938', '0939', '0941'],
    mci: ['0910', '0911', '0912', '0913', '0914', '0915', '0916', '0917', '0918', '0919', '0990', '0991', '0992'],
    talia: ['0932'],
    rightel: ['0920', '0921', '0922'],
    spadan: ['0931'],
    tkc: ['0934'],
    shatel: ['099810', '099811', '099812'],
    aptel: ['099910', '099911', '099913'],
    azartel: ['099914'],
    samantel: ['099996', '099997', '099998', '099999'],
    lotustel: ['09990'],
    anarestan: ['09944']
};
/**
 * This service using for validating phone numbers sold by Iranian Mobile operators.
 *
 * You can get operator name and validation result of a phone number by methods of this service.
 */
class MobilePhoneNumberService {
    /**
     * This method only checks if phoneNumber follows valid pattern. means starting by +98 or 0, then a 9 digit and then 9 digits.
     * This method will not check validity of the phoneNumber code and can accept invalid strings those only locking like phone numbers and
     * in reality are not a valid phone number.
     *
     * @link isValidPhoneNumber
     * @param phoneNumber Digits in this string should be english digits.
     */
    isPhoneNumberPatternValid(phoneNumber) {
        const generalRegex = new RegExp(/^((\+98|0)9\d{9})$/);
        return generalRegex.test(phoneNumber);
    }
    /**
     * Removes +98 from the phoneNumber and replaces it with a 0 character.
     *
     * @param phoneNumber Digits in this string should be english digits.
     */
    normalizePhoneNumber(phoneNumber) {
        return phoneNumber.replace('+98', '0');
    }
    /**
     * Checks all codes of all operators in MobileCodes object. IF finds a matched code, returns persian or english name of that operator from
     * operatorsNames enum.
     *
     * @return operator name in english or persian according to the nameInEnglish parameter
     * @throws InvalidMobileOperatorError if pattern is correct but operator code is not valid.
     * @throws InvalidMobilePhoneNumberError If pattern is not correct
     * @param phoneNumber Digits in this string should be english digits.
     * @param nameInEnglish if true, operator name will return in English. if false operator name will return in persian
     * @link MobileCodes
     * @link operatorsNames
     */
    getPhoneNumberOperator(phoneNumber, nameInEnglish = false) {
        if (!this.isPhoneNumberPatternValid(phoneNumber)) {
            throw new InvalidMobilePhoneNumberError();
        }
        const NormalizedPhoneNumber = this.normalizePhoneNumber(phoneNumber);
        for (const operator of Object.keys(MobileCodes)) {
            for (const code of MobileCodes[operator]) {
                if (NormalizedPhoneNumber.indexOf(code) === 0) {
                    return nameInEnglish ? operator : operatorsNames[operator];
                }
            }
        }
        throw new InvalidMobileOperatorError();
    }
    /**
     * @return If phoneNumber follows correct pattern and has valid operator, return true. Otherwise returns false.
     * @param phoneNumber Digits in this string should be english digits.
     * @link getPhoneNumberOperator
     */
    isValidPhoneNumber(phoneNumber) {
        try {
            this.getPhoneNumberOperator(phoneNumber);
            return true;
        }
        catch (e) {
            return false;
        }
    }
}
MobilePhoneNumberService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: MobilePhoneNumberService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
MobilePhoneNumberService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: MobilePhoneNumberService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: MobilePhoneNumberService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * When mobile phone number is not valid this error will throw.
 */
class InvalidNationalCodeError extends Error {
    constructor(nationalCode = '') {
        super(`The national code value (${nationalCode}) is not a valid national code.`);
    }
}

/**
 * For validating Iranian National code (number), you can use this service in your components or every other places using DI or not.
 */
class NationalCodeService {
    /**
     * This method adds zero character at the beginning of the input until the length of the result reach to 10.
     *
     * This method removes spaces from right and left sides of the input.
     *
     * ATTENTION: This method doesn't any validation about correctness of the input if it has correct length (between 8 and 10).
     *
     * @param nationalCode a number with at most 10 digits or a string with at most 10 character.
     * @return a string with length equal to 10.
     * @throws InvalidNationalCodeError when length of the input value is greater than 10.
     */
    normalize(nationalCode) {
        if (!nationalCode) {
            throw new InvalidNationalCodeError();
        }
        let normalizedNationalCode = nationalCode.toString();
        normalizedNationalCode = normalizedNationalCode.trim();
        const nationalCodeLength = normalizedNationalCode.length;
        if (nationalCodeLength > 10 || nationalCodeLength < 8) {
            throw new InvalidNationalCodeError(normalizedNationalCode);
        }
        return '0'.repeat(10 - nationalCodeLength) + normalizedNationalCode;
    }
    /**
     * This method first normalizes the input using `normalize` method and implicitly checks input length, So you DO NOT NEED to
     * normalize input before, but this will not make any bad effect.
     *
     * [Then checking validation of the input using algorithm described in this link]{@link http://www.aliarash.com/article/codemeli/codemeli.htm}
     *
     * @param nationalCode
     */
    isValid(nationalCode) {
        if (!nationalCode) {
            return false;
        }
        let NormalizedNationalCode = nationalCode.toString();
        if (this._isRepetitive(NormalizedNationalCode)) {
            return false;
        }
        try {
            NormalizedNationalCode = this.normalize(NormalizedNationalCode); // Normalizing the input and checking input length validation implicitly.
        }
        catch (e) {
            return false; // Input length is not in the valid range.
        }
        let multiplicationResult = 0;
        for (let i = 0; i < 9; i++) {
            multiplicationResult += parseInt(NormalizedNationalCode.charAt(i), 10) * (10 - i);
        }
        const reminder = multiplicationResult % 11;
        const controlNum = parseInt(NormalizedNationalCode.charAt(9), 10);
        let desiredControlNum = reminder;
        if (reminder > 1) {
            desiredControlNum = 11 - reminder;
        }
        return desiredControlNum === controlNum;
    }
    /**
     *
     * [We are using for loop instead of match or split because it has better performance according to this link]{@link https://stackoverflow.com/a/46312614/5400597}
     *
     * @return true if all characters of the `input` are equal and input length is greater than 1. Otherwise returns false.
     * @param input
     */
    _isRepetitive(input) {
        if (input.length < 2) {
            return false;
        }
        const firstChar = input.charAt(0);
        for (let i = 1; i < input.length; i++) {
            if (input.charAt(i) !== firstChar) {
                return false;
            }
        }
        return true;
    }
}
NationalCodeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: NationalCodeService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
NationalCodeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: NationalCodeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: NationalCodeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/*
 * Public API Surface of ngx-persian
 */

/**
 * Generated bundle index. Do not edit.
 */

export { EnNumPipe, FaNumPipe, IRCurrencies, IRCurrencyPipe, JDate, JalaliDateCalculatorService, JalaliDateValidatorService, JdatePipe, MobileCodes, MobilePhoneNumberService, NationalCodePipe, NationalCodeService, NgxPersianModule, PersianLetterDirective, PersianLetterService, PersianNumberService, PersianNumbersDirective, operatorsNames, persianLettersValidator, persianNumbersValidator };
